import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import DeathCard from "@/components/game/DeathCard";
import { api } from "@/lib/api";
import { Flame, LogIn, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INTENDED_CLASS_KEY = "ember_intended_class_v1";
const INTENDED_UTM_KEY = "ember_intended_utm_v1";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("data-og-injected", "1");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    el.setAttribute("data-og-injected", "1");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
}

function removeInjectedMeta() {
  document.head
    .querySelectorAll('meta[data-og-injected="1"]')
    .forEach((el) => el.remove());
}

export default function CardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState(null);
  const [me, setMe] = useState(null); // {authed: bool, user?: {...}}
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/cards/${id}`)
      .then((r) => setCard(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "Card not found." : "Unable to load card."));
  }, [id]);

  useEffect(() => {
    // Quiet auth probe — do not redirect on 401.
    api.get("/auth/me")
      .then((u) => setMe({ authed: true, user: u }))
      .catch(() => setMe({ authed: false }));
  }, []);

  // Dynamic <title> + OpenGraph / Twitter meta injection so shared links
  // unfurl beautifully on Slack, Discord, Twitter, etc.
  useEffect(() => {
    if (!card) return;
    const outcome = card.outcome === "victory" ? "slew the Ember King on" : "fell on";
    const title = `${card.user_name} ${outcome} floor ${card.floors_cleared} · Champions of the Dawn Vanguard`;
    const desc = card.final_narrative || `A ${card.class_name} descended into the Ember King's keep.`;
    const abs = (p) => (p && p.startsWith("http") ? p : `${BACKEND_URL}${p || ""}`);
    const image = card.portrait_url ? abs(card.portrait_url) : "";
    const url = `${window.location.origin}/card/${id}`;

    document.title = title;
    upsertMeta("meta[name='description']", { name: "description", content: desc });
    upsertMeta("meta[property='og:type']", { property: "og:type", content: "article" });
    upsertMeta("meta[property='og:title']", { property: "og:title", content: title });
    upsertMeta("meta[property='og:description']", { property: "og:description", content: desc });
    upsertMeta("meta[property='og:url']", { property: "og:url", content: url });
    if (image) upsertMeta("meta[property='og:image']", { property: "og:image", content: image });
    upsertMeta("meta[name='twitter:card']", { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    upsertMeta("meta[name='twitter:title']", { name: "twitter:title", content: title });
    upsertMeta("meta[name='twitter:description']", { name: "twitter:description", content: desc });
    if (image) upsertMeta("meta[name='twitter:image']", { name: "twitter:image", content: image });

    return () => {
      document.title = "Champions of the Dawn Vanguard · Tactical Co-Op Dungeon Crawler";
      removeInjectedMeta();
    };
  }, [card, id]);

  const startAsThisHero = async () => {
    if (!card?.class_key) return;
    // Capture UTM attribution from the current URL so we can credit this run to the share source.
    const q = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: q.get("utm_source") || "share",
      utm_medium: q.get("utm_medium") || "card",
      utm_campaign: q.get("utm_campaign") || `${card.class_key}_${card.outcome || "run"}`,
    };
    try {
      sessionStorage.setItem(INTENDED_CLASS_KEY, card.class_key);
      sessionStorage.setItem(INTENDED_UTM_KEY, JSON.stringify(utm));
    } catch { /* noop */ }
    if (me?.authed) {
      navigate("/dashboard");
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    setStarting(true);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen px-4 py-16 flex flex-col items-center" data-testid="card-page">
      <div className="max-w-3xl w-full">
        <div className="mb-10 text-center">
          <div className="text-[10px] tracking-[0.4em] ash-text">A ROGUE-LITE ADVENTURE</div>
          <Link to="/" className="font-heading text-3xl text-[#f0e6d2] hover:text-white transition-colors">
            The <span className="gold-text italic">Ember</span> Below
          </Link>
        </div>
        {err && <div className="panel p-8 text-center ash-text" data-testid="card-error">{err}</div>}
        {!err && !card && (
          <div className="panel p-8 text-center ash-text">Unfolding the tale...</div>
        )}
        {card && <DeathCard card={card} />}

        {card && (
          <div className="mt-8 panel p-6 text-center" data-testid="continue-as-hero-panel"
               style={{ boxShadow: "0 0 30px rgba(212,175,55,0.2)" }}>
            <div className="flex items-center justify-center gap-2 gold-text">
              <Flame size={14} />
              <span className="text-[10px] tracking-[0.4em]">TAKE UP THE MANTLE</span>
              <Flame size={14} />
            </div>
            <h3 className="font-heading text-3xl mt-2 text-[#f0e6d2]">
              Continue as a <span className="gold-text italic">{card.class_name}</span>
            </h3>
            <p className="font-narrative text-base ash-text italic mt-2 max-w-xl mx-auto">
              {card.user_name} could not finish the descent. Perhaps you can.
            </p>
            <button
              onClick={startAsThisHero}
              disabled={starting || me === null}
              data-testid="continue-as-hero-btn"
              className="btn-primary mt-5 inline-flex items-center gap-2"
            >
              {starting ? "OPENING THE GATE..." :
                me?.authed ? <><Play size={14} /> BEGIN YOUR DESCENT</> :
                            <><LogIn size={14} /> SIGN IN & BEGIN</>}
            </button>
            {me?.authed && (
              <p className="text-[10px] ash-text tracking-widest mt-2" data-testid="continue-signed-in-hint">
                SIGNED IN AS {me.user?.name?.toUpperCase()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { INTENDED_CLASS_KEY, INTENDED_UTM_KEY };
